import { Component, computed, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { PageComponent } from '../../../infrastructure/shared/components/page/page';
import { PackagesClient, CompanyPackageDto } from '../../../infrastructure/shared/model/fss.api';
import { AlertService } from '../../../infrastructure/shared/services/alert.service';
import { LoaderService } from '../../../infrastructure/shared/components/loader/loader.service';
import { LocationCompanyService } from '../../../infrastructure/shared/services/location-company.service';
import { Cube01 } from '../../../infrastructure/shared/icons/cube-01/cube-01';
import { ChevronRight } from '../../../infrastructure/shared/icons/chevron-right/chevron-right';
import { CreditCard02 } from '../../../infrastructure/shared/icons/credit-card-02/credit-card-02';
import { File06 } from '../../../infrastructure/shared/icons/file-06/file-06';
import { Stars02 } from '../../../infrastructure/shared/icons/stars-02/stars-02';
import { BuyPackageDialog } from './buy-package-dialog/buy-package-dialog';
import {
  PackageCard,
  PackageCardSection,
  getPackageSavings,
  isPopularPackage,
} from '../../../infrastructure/shared/components/package-card/package-card';

@Component({
  standalone: true,
  selector: 'app-company-packages-page',
  imports: [
    PageComponent,
    Cube01,
    ChevronRight,
    CreditCard02,
    File06,
    Stars02,
    BuyPackageDialog,
    PackageCard,
  ],
  templateUrl: './company-packages-page.html',
  styleUrl: './company-packages-page.scss',
})
export class CompanyPackagesPage {
  private _client = inject(PackagesClient);
  private _alert = inject(AlertService);
  private _loader = inject(LoaderService);
  private _location = inject(Location);
  private _locationCompany = inject(LocationCompanyService);

  packages = signal<CompanyPackageDto[]>([]);
  buyingPackageId = signal<string | null>(null);
  loading = signal(true);
  dialogVisible = signal(false);
  selectedPkg = signal<CompanyPackageDto | null>(null);
  isHowItWorksExpanded = signal(true);

  cards = computed(() => {
    const packages = this.packages();
    return packages.map((pkg, i) => ({
      pkg,
      name: this.getPackageName(pkg),
      savings: getPackageSavings(pkg, packages),
      isPopular: isPopularPackage(i, packages.length),
      sections: this._getSections(pkg),
    }));
  });


  constructor() {
    this.load();
  }

  load() {
    this.loading.set(true);
    this._loadPackages().then((res) => {
      this.packages.set(res.result.packages ?? []);
      this.loading.set(false);
    });
  }

  getPackageName(pkg: CompanyPackageDto): string {
    const h = pkg.hours ?? 0;
    if (h <= 6) return `Studio ${h}`;
    if (h <= 15) return `Pro ${h}`;
    return `Partner ${h}`;
  }

  private _getSections(pkg: CompanyPackageDto): PackageCardSection[] {
    return [
      {
        title: 'Detalji paketa:',
        items: [
          `Do ${pkg.numOfUsers} učesnika`,
          `Prenos ${pkg.nextMonthHourTransfer}h u naredni mesec`,
          `Važi ${pkg.daysValid} dana od dana aktivacije`,
        ],
      },
      {
        title: 'Zakazivanje termina:',
        // No cutoff (0) means the appointment can be changed at any time — a benefit, not a restriction.
        items: [
          `Zakazivanje termina do ${pkg.daysBookableInAdvance} dana unapred`,
          ...(pkg.changeHourCutoff ? [] : ['Promena termina: u bilo kom trenutku pre termina']),
        ],
        rules: pkg.changeHourCutoff
          ? [`Promena termina: najkasnije ${pkg.changeHourCutoff}h pre termina`]
          : [],
      },
      {
        title: 'Politika nedolaska:',
        items: (pkg.noShowPenalty ?? 0) === 0 ? ['Termin se ne naplaćuje.'] : [],
        rules:
          (pkg.noShowPenalty ?? 0) === 0
            ? []
            : [
                (pkg.noShowPenalty ?? 0) >= 100
                  ? 'Termin se naplaćuje u celosti.'
                  : `Naplaćuje se ${pkg.noShowPenalty}% cene termina.`,
              ],
      },
    ];
  }

  openBuyDialog(pkg: CompanyPackageDto): void {
    this.selectedPkg.set(pkg);
    this.dialogVisible.set(true);
  }

  buy(pkg: CompanyPackageDto) {
    if (!pkg.id || this.buyingPackageId()) return;
    this.buyingPackageId.set(pkg.id);

    this._loader.setLoadingText = 'Slanje zahteva...';
    this._loader.setShowLoader = true;

    this._buyPackage(pkg.id)
      .then((res) => {
        this._alert.addSuccessMsg(res.result);
        this._locationCompany.routeToPackages();
      })
      .finally(() => {
        this.buyingPackageId.set(null);
      });
  }

  goBack(): void {
    this._location.back();
  }

  private async _loadPackages() {
    return await firstValueFrom(this._client.getCompanyPackages());
  }

  private async _buyPackage(id: string) {
    return await firstValueFrom(this._client.buyPackage(id));
  }
}
